import { Flex, Text, Box } from '@chakra-ui/react';
import { Checkbox, FormFeedback, Link, OutlineCard } from '@morressier/alembic';
import { useFormContext } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import {
  FormFieldARRIVEFiles,
  FormFieldCONSORTFiles,
  FormFieldFileUploadArray,
  FormFieldStandardReportingGuidelines,
  FormFieldStandardReportingGuidelinesDetails,
  FormFieldTextInput,
  OptionsField
} from '@publishing-engine/domain/types/article';
import { get } from 'lodash';
import { useEffect } from 'react';
import { FormFieldMultipleFileUpload } from 'src/components/FormField';
import { deleteUploadedFile } from 'src/api/queries';
import { StandardReportingGuidelinesRadioGroup } from './StandardReportingGuidelinesRadioGroup';
import FormFieldTextAreaField from 'src/components/FormField/FormFieldTextArea';

export type Guideline = {
  label: string;
  href: string;
  note?: string;
};

export const parseGuidelines = (guidelines: OptionsField): Guideline[] => {
  const parseGuidelinesArray: Guideline[] = Object.values(guidelines).map(
    ({ main }) => {
      const match = main.match(/\[(.*?)\]\((.*?)\)\s*(.*)/);

      let note = match?.[3]?.trim() || '';
      // remove surrounding parentheses if present
      if (note.startsWith('(') && note.endsWith(')')) {
        note = note.slice(1, -1);
      }

      return {
        label: match?.[1] || '',
        href: match?.[2] || '',
        note
      };
    }
  );
  return parseGuidelinesArray;
};

export function convertAllowedFormatsToString(
  allowedFileFormats: Record<string, string[]> | undefined
): string {
  if (!allowedFileFormats) return '';

  const formats = Array.from(
    new Set(
      (Object.values(allowedFileFormats) as string[][])
        .flat()
        .map(ext => ext.replace('.', '').toUpperCase())
    )
  );

  return formats.join(', ');
}

const StandardReportingGuidelines = ({
  config,
  CONSORTFilesConfig,
  ARRIVEFilesConfig,
  submissionId,
  StandardReportingGuidelinesDetails,
  StandardReportingGuidelinesFileDetails,
  StandardReportingGuidelinesTextConfig
}: {
  config: FormFieldStandardReportingGuidelines;
  CONSORTFilesConfig: FormFieldCONSORTFiles | null;
  ARRIVEFilesConfig: FormFieldARRIVEFiles | null;
  submissionId: string;
  StandardReportingGuidelinesDetails: FormFieldStandardReportingGuidelinesDetails | null;
  StandardReportingGuidelinesFileDetails: FormFieldFileUploadArray<'fileSet.StandardReportingGuidelinesFileDetails'> | null;
  StandardReportingGuidelinesTextConfig: FormFieldTextInput<'preference.standardReportingGuidelinesText'> | null;
}) => {
  const {
    formState: { errors },
    control,
    watch,
    setValue,
    unregister
  } = useFormContext<ISubmissionFormData>();
  const {
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;
  const error = get(errors, field);

  const errorStandardReportingGuidelinesDetails =
    StandardReportingGuidelinesDetails
      ? get(errors, 'preference.standardReportingGuidelinesDetails')
      : undefined;

  const rawValue = watch('preference.standardReportingGuidelinesDetails');
  const CONSORTFile = watch('fileSet.CONSORTFiles') ?? [];
  const ARRIVEFiles = watch('fileSet.ARRIVEFiles') ?? [];
  const radioValue = watch('preference.standardReportingGuidelines');
  const value: string[] = Array.isArray(rawValue)
    ? rawValue
    : rawValue
    ? [rawValue]
    : [];

  const guidelines = parseGuidelines(
    StandardReportingGuidelinesDetails?.params.labels.options ?? {}
  );

  const handleGuidelineToggle = (label: string, checked: boolean) => {
    const currentValues = Array.isArray(rawValue)
      ? [...rawValue]
      : rawValue
      ? [rawValue]
      : [];

    const nextValues = checked
      ? Array.from(new Set([...currentValues, label]))
      : currentValues.filter(item => item !== label);

    setValue('preference.standardReportingGuidelinesDetails', nextValues, {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  useEffect(() => {
    if (!value.includes('CONSORT Statement') && CONSORTFile.length > 0) {
      CONSORTFile.forEach(
        item => item.fileId && deleteUploadedFile(item.fileId)
      );
      setValue('fileSet.CONSORTFiles', []);
    }
    if (!value.includes('ARRIVE') && ARRIVEFiles.length > 0) {
      ARRIVEFiles.forEach(
        item => item.fileId && deleteUploadedFile(item.fileId)
      );
      setValue('fileSet.ARRIVEFiles', []);
    }
  }, [radioValue, value, setValue]);

  useEffect(() => {
    if (!radioValue) {
      unregister('preference.standardReportingGuidelinesDetails');
      unregister('fileSet.CONSORTFiles');
      unregister('fileSet.ARRIVEFiles');
      unregister('fileSet.StandardReportingGuidelinesFileDetails');
    } else {
      unregister('preference.standardReportingGuidelinesText');
    }
  }, [radioValue, unregister]);

  return (
    <OutlineCard width="100%" padding={4}>
      <Flex flexDirection="column" gap={4} width="100%">
        <FieldLabel
          isRequired={validations.required}
          label={labels.main}
          subText={labels.subtitle}
          helpText={labels.helpText}
        />

        <StandardReportingGuidelinesRadioGroup
          control={control}
          name={field}
          options={[
            {
              value: true,
              children: (
                <>
                  <Text textStyle="textBody">
                    {labels.options['true']?.main}
                  </Text>
                  {radioValue && (
                    <>
                      {guidelines.length > 0
                        ? guidelines.map(item => (
                            <Flex key={item.label} direction="column">
                              <Box>
                                <Checkbox
                                  isChecked={value.includes(item.label)}
                                  value={item.label}
                                  name="preference.standardReportingGuidelinesDetails"
                                  ariaLabel={item.label}
                                  id={item.label}
                                  disabled={!radioValue}
                                  m={2}
                                  onChange={e =>
                                    handleGuidelineToggle(
                                      item.label,
                                      e.currentTarget.checked
                                    )
                                  }
                                >
                                  <Link
                                    href={item.href}
                                    isExternal
                                    variant="primary"
                                  >
                                    {item.label}
                                  </Link>
                                  <Text as="span" ml={2}>
                                    {item.note}
                                  </Text>
                                </Checkbox>
                              </Box>

                              {value.includes('CONSORT Statement') &&
                                item.label === 'CONSORT Statement' && (
                                  <Flex
                                    flexDirection="column"
                                    bgColor="surfaceSecondaryFill"
                                    m={2}
                                    p={4}
                                    cursor="default"
                                  >
                                    {CONSORTFilesConfig && (
                                      <FormFieldMultipleFileUpload
                                        submissionId={submissionId}
                                        config={CONSORTFilesConfig}
                                        width="50%"
                                        helperText={convertAllowedFormatsToString(
                                          CONSORTFilesConfig?.params
                                            ?.validations?.allowedFileFormats
                                        )}
                                      />
                                    )}
                                  </Flex>
                                )}

                              {value.includes('ARRIVE') &&
                                item.label === 'ARRIVE' && (
                                  <Flex
                                    flexDirection="column"
                                    bgColor="surfaceSecondaryFill"
                                    m={2}
                                    p={4}
                                    cursor="default"
                                  >
                                    {ARRIVEFilesConfig && (
                                      <FormFieldMultipleFileUpload
                                        submissionId={submissionId}
                                        config={ARRIVEFilesConfig}
                                        width="50%"
                                        helperText={convertAllowedFormatsToString(
                                          ARRIVEFilesConfig?.params?.validations
                                            ?.allowedFileFormats
                                        )}
                                      />
                                    )}
                                  </Flex>
                                )}
                            </Flex>
                          ))
                        : StandardReportingGuidelinesFileDetails && (
                            <Flex
                              flexDirection="column"
                              padding={{ base: '4', md: '4' }}
                            >
                              <FormFieldMultipleFileUpload
                                submissionId={submissionId}
                                config={StandardReportingGuidelinesFileDetails}
                                width={{ base: '100%', md: '50%' }}
                                helperText={convertAllowedFormatsToString(
                                  StandardReportingGuidelinesFileDetails?.params
                                    ?.validations?.allowedFileFormats
                                )}
                              />
                            </Flex>
                          )}
                    </>
                  )}

                  {radioValue && errorStandardReportingGuidelinesDetails && (
                    <FormFeedback variant="error">
                      Indicate your preference for standard reporting guidelines
                      before proceeding
                    </FormFeedback>
                  )}
                </>
              )
            },
            {
              value: false,
              children: (
                <>
                  <Text textStyle="textBody">
                    {labels.options['false']?.main}
                  </Text>
                  <Text>{labels.options['false']?.secondary}</Text>

                  {radioValue === false &&
                    StandardReportingGuidelinesTextConfig && (
                      <FormFieldTextAreaField
                        config={StandardReportingGuidelinesTextConfig}
                        placeholder="Provide any additional details"
                      />
                    )}
                </>
              )
            }
          ]}
        />

        {error ? (
          <FormFeedback variant="error">
            Indicate your preference for standard reporting guidelines before
            proceeding
          </FormFeedback>
        ) : null}
      </Flex>
    </OutlineCard>
  );
};

export default StandardReportingGuidelines;
